import { Task, TaskHandler, TaskScheduler } from 'app/utils/async/TaskScheduler';
import _, { uniqueId } from 'lodash';

type PendingTask = { handler: TaskHandler; task: Task };

export default class ParallelTaskScheduler implements TaskScheduler {
  private availableExecutionSlots: number;
  private pendingTasks: PendingTask[][];
  private stopped: boolean = false;

  constructor(maxParallelism: number = 1) {
    this.availableExecutionSlots = maxParallelism;
    this.pendingTasks = [[]];
  }

  schedule(task: () => Promise<any>, priority: number = 0): TaskHandler {
    if (this.stopped) return;

    const handler = uniqueId();
    if (this.availableExecutionSlots) {
      this.startTaskExecution(task);
    } else {
      if (Math.round(priority) > this.pendingTasks.length) {
        this.pendingTasks.push(
          ..._.times(Math.round(priority) - this.pendingTasks.length + 1, () => [])
        );
      }
      this.pendingTasks[priority].push({ handler, task });
    }
    return handler;
  }

  forceExecution(handler: TaskHandler) {
    if (!handler) return;

    let requestedTask: PendingTask;
    for (let i = 0; i < this.pendingTasks.length; i++) {
      requestedTask = _.find(this.pendingTasks[i], { handler });
      if (requestedTask) {
        _.remove(this.pendingTasks[i], { handler });
      }
    }
    this.startTaskExecution(requestedTask?.task);
  }

  stop() {
    this.stopped = true;
    this.pendingTasks = [];
  }

  private startTaskExecution(task?: Task) {
    if (!task) return;

    this.availableExecutionSlots -= 1;
    task().finally(() => {
      this.availableExecutionSlots += 1;

      this.startTaskExecution(this.shiftNextPendingTask());
    });
  }

  shiftNextPendingTask() {
    for (const tasks of this.pendingTasks) {
      if (tasks.length > 0) {
        return tasks.shift()?.task;
      }
    }
    return undefined;
  }
}
